import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Manifest } from '@/types/manifest';
import { PlayerEngine, type PlayerStatus } from '@/lib/player-engine';
import { PlayerStage } from '@/components/player/PlayerStage';
import { PlayerRecoveryOverlay } from '@/components/player/PlayerRecoveryOverlay';
import { buildAssetCacheKey, buildLegacyCacheKey, getCachedResponse } from '@/lib/asset-cache';
import {
  clearStoredToken,
  parseTokenFromUrl,
  readStoredToken,
  scrubTokenFromUrl,
  writeStoredToken,
} from '@/lib/player-token-storage';

export function PlayerPage() {
  const { screenId } = useParams<{ screenId: string }>();

  // Resolve the token once, on first mount, in this priority order:
  //   1. URL fragment `#t=` / `#token=`
  //   2. URL query    `?t=` / `?token=`  (legacy / backward compat)
  //   3. Locally persisted token for this screen
  //
  // Whichever URL form is used, we immediately scrub it so the raw token
  // never survives in window.location, browser history, screenshots, or
  // same-origin Referer headers. We also persist the token locally so
  // reloading `/play/:screenId` (with no token in the URL) keeps working.
  const playerToken = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const fromUrl = parseTokenFromUrl(window.location);
    if (fromUrl && screenId) {
      writeStoredToken(screenId, fromUrl);
      scrubTokenFromUrl();
      return fromUrl;
    }
    if (screenId) {
      const stored = readStoredToken(screenId);
      if (stored) return stored;
    }
    // No URL token, no stored token: leave empty so the missing-token UI
    // renders.
    if (typeof window !== 'undefined') scrubTokenFromUrl();
    return '';
  }, [screenId]);

  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [revisionId, setRevisionId] = useState<string | null>(null);
  const [status, setStatus] = useState<PlayerStatus>({ meta: null, manifest: null });
  const engineRef = useRef<PlayerEngine | null>(null);

  useEffect(() => {
    if (!screenId) return;
    if (!playerToken) return;
    document.documentElement.classList.add('dark');
    document.body.classList.add('player-root');

    const engine = new PlayerEngine({
      screenId,
      playerToken,
      onStatusChange: setStatus,
      onManifestReady: (m, revId) => {
        setManifest(m);
        setRevisionId(revId);
      },
    });
    engineRef.current = engine;
    void engine.start();

    return () => {
      engine.stop();
      document.body.classList.remove('player-root');
    };
  }, [screenId, playerToken]);

  // Resolver: prefer cached blob URL when available, otherwise fall back to
  // the signed remote URL embedded in the manifest. The engine warmed the
  // cache for us when the active revision became "ready".
  const resolveUrl = async (
    url: string,
    hint: { kind: 'asset' | 'overlay'; key: string; contentHash?: string | null },
  ): Promise<string> => {
    const candidates: string[] = [];
    if (hint.contentHash) candidates.push(buildAssetCacheKey(hint.contentHash));
    if (revisionId) {
      const legacyId = hint.kind === 'overlay' ? `overlay:${hint.key}` : hint.key;
      candidates.push(buildLegacyCacheKey(legacyId, revisionId));
    }
    for (const cacheKey of candidates) {
      const cached = await getCachedResponse(cacheKey);
      if (cached) {
        const blob = await cached.blob();
        return URL.createObjectURL(blob);
      }
    }
    return url;
  };

  if (!screenId) {
    return <FullscreenMessage>Missing screen id.</FullscreenMessage>;
  }

  if (!playerToken) {
    return (
      <>
        <FullscreenMessage>
          <div className="space-y-3 text-center">
            <div>Missing player token.</div>
            <div className="text-xs text-white/50">
              Open the app, sign in, and use{' '}
              <span className="font-mono">Players → Pair Raspberry Pi Player</span> after
              this device shows a code on <span className="font-mono">/provision</span>.
            </div>
            <button
              type="button"
              onClick={() => window.location.replace('/provision')}
              className="rounded border border-white/20 px-3 py-1 text-xs text-white/80 hover:bg-white/10"
              data-testid="goto-provision"
            >
              Go to /provision
            </button>
          </div>
        </FullscreenMessage>
        <PlayerRecoveryOverlay screenId={screenId} status={status} />
      </>
    );
  }

  if (status.fatal) {
    const isToken =
      status.error === 'invalid_token' ||
      status.error === 'revoked' ||
      status.error === 'missing_token';
    const msg = isToken
      ? status.error === 'missing_token'
        ? 'Missing player token.'
        : 'Invalid or revoked player token.'
      : `Player error: ${status.error}`;
    return (
      <>
        <FullscreenMessage>
          <div className="space-y-3 text-center">
            <div>{msg}</div>
            {isToken && (
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (screenId) clearStoredToken(screenId);
                    // Reload to /play/:screenId without any token; the UI will
                    // re-render with the missing-token message.
                    window.location.replace(`/play/${screenId}`);
                  }}
                  className="rounded border border-white/20 px-3 py-1 text-xs text-white/80 hover:bg-white/10"
                  data-testid="reset-player-token"
                >
                  Reset saved player token
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (screenId) clearStoredToken(screenId);
                    window.location.replace('/provision');
                  }}
                  className="rounded border border-white/20 px-3 py-1 text-xs text-white/80 hover:bg-white/10"
                  data-testid="reset-and-repair"
                >
                  Re-pair this device
                </button>
              </div>
            )}
          </div>
        </FullscreenMessage>
        <PlayerRecoveryOverlay screenId={screenId} status={status} />
      </>
    );
  }

  if (!manifest) {
    return (
      <>
        <FullscreenMessage>
          {status.error ? <span className="text-white/70">{status.error}</span> : 'Loading…'}
        </FullscreenMessage>
        <PlayerRecoveryOverlay screenId={screenId} status={status} />
      </>
    );
  }

  if (manifest.playlist.length === 0) {
    return (
      <>
        <FullscreenMessage>No published playlist</FullscreenMessage>
        <PlayerRecoveryOverlay screenId={screenId} status={status} />
      </>
    );
  }

  return (
    <div className="player-root">
      <PlayerStage
        manifest={manifest}
        resolveUrl={resolveUrl}
        playEvents={engineRef.current?.getPlayEventsBuffer() ?? null}
        revisionId={revisionId}
      />
      <PlayerRecoveryOverlay screenId={screenId} status={status} />
    </div>
  );
}

function FullscreenMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="player-root flex items-center justify-center bg-black text-white/70">
      <div className="text-sm">{children}</div>
    </div>
  );
}
